import React, { createContext, ReactNode, useContext, useState } from "react";

import _ from "lodash";
import { Project, ProjectTask } from "./";

import { serialize, update } from "serializr";
import { ApiService } from "../api";
import { ProjectTaskComment } from "./ProjectTaskComment";

import { ProjectTaskChecklist } from "./ProjectTaskChecklist";
import { ProjectTaskChecklistItem } from "./ProjectTaskChecklistItem";

import { Attachment } from "../attachments";
import { ProjectService } from "./ProjectService";
import { ProjectTaskService } from "./ProjectTaskService";
import { ProjectTaskTimelog } from "./ProjectTaskTimelog";

interface IProjectContextType {
	tasks?: ProjectTask[];
	create?: () => Promise<Project | undefined>;
	save?: () => Promise<Project | undefined>;
	saveMembers?: (
		action: string,
		userId: number,
		memberType: string,
	) => Promise<Project | undefined>;
	find?: (projectId: number) => Promise<Project | undefined>;
	uploadFiles?: (files: File[], task?: ProjectTask) => void;
	deleteFile?: (attachment: Attachment) => void;
	project?: Project;
	findTasks?: (search?: string) => Promise<ProjectTask[] | undefined>;
	changeTaskStatus?: (
		taskId: number,
		position: number,
		status: string,
	) => Promise<ProjectTask>;
	createTask?: (
		title: string,
		description: string,
		status: string,
	) => Promise<ProjectTask | undefined>;
	findTask?: (taskId: number) => Promise<ProjectTask>;
	saveTask?: (task: ProjectTask) => Promise<ProjectTask>;
	saveTaskMembers?: (
		task: ProjectTask,
		action: string,
		userId?: number,
	) => Promise<ProjectTask>;
	logTimeOnTask?: (
		task: ProjectTask,
		timelog: ProjectTaskTimelog,
	) => Promise<ProjectTask>;
	setProject?: React.Dispatch<any>;
	setComment?: React.Dispatch<any>;
	setTasks?: React.Dispatch<any>;

	findProjectTaskComments?: (taskId: number) => Promise<ProjectTaskComment[]>;
	createProjectTaskComment?: (
		task: ProjectTask,
		comment: ProjectTaskComment,
	) => Promise<ProjectTaskComment>;
	updateProjectTaskComment?: (
		task: ProjectTask,
		comment: ProjectTaskComment,
	) => Promise<ProjectTaskComment>;

	saveAddProjectTaskChecklist?: (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
	) => Promise<ProjectTaskChecklist>;

	deleteProjectTaskChecklist?: (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
	) => Promise<ProjectTaskChecklist[]>;

	saveUpdateProjectTaskChecklist?: (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
	) => Promise<ProjectTaskChecklist>;

	saveAddProjectTaskChecklistItem?: (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
		item: ProjectTaskChecklistItem,
	) => Promise<ProjectTaskChecklistItem>;

	deleteProjectTaskChecklistItem?: (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
		item: ProjectTaskChecklistItem,
	) => Promise<ProjectTaskChecklistItem[]>;

	saveUpdateProjectTaskChecklistItem?: (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
		item: ProjectTaskChecklistItem,
	) => Promise<ProjectTaskChecklistItem>;

	toggleWatchingTask?: (task: ProjectTask) => Promise<ProjectTask>;
	toggleWatchingProject?: () => Promise<Project | undefined>;
}

interface IProps {
	children: ReactNode;
}

interface IActionMap {
	[key: string]: string;
}

const ProjectContext = createContext<IProjectContextType>({});

export const ProjectProvider = (props: IProps) => {
	const [tasks, setTasks] = useState<ProjectTask[]>();
	const [project, setProject] = useState<Project>();

	const api = new ApiService();
	const taskService = new ProjectTaskService();
	const projectService = new ProjectService();

	/**
	 * Find a single project by id
	 * @param projectId
	 */
	const find = async (projectId: number) => {
		const projectData = await api.get(`/api/v1/project/${projectId}`);
		const projectJson = await projectData.json();

		const projectModel = Project.fromJson(projectJson);

		setProject(projectModel);

		return project;
	};

	/**
	 * Find all tasks for a project
	 */
	const findTasks = async (search?: string) => {
		let params;
		if (search) {
			params = { search };
		}

		if (!project) {
			return;
		}

		const taskData = await api.get(
			`/api/v1/project/${project.id}/tasks`,
			params,
		);
		const projectTasks = await taskData.json();

		setTasks(ProjectTask.fromJsonArray(projectTasks));
		return tasks;
	};

	/**
	 * Create a new project
	 */
	const create = async () => {
		if (!project) {
			return;
		}
		const projectData = await api.post(
			`/api/v1/projects/${project.accountId}`,
			serialize(Project, project),
		);

		const newProject = await projectData.json();

		if (projectData.status !== 200) {
			throw Error(newProject);
		}

		const projectModel = Project.fromJson(newProject);

		setProject(projectModel);

		return projectModel;
	};

	const save = async () => {
		if (!project) {
			return;
		}
		const projectData = await api.post(
			`/api/v1/project/${project.id}`,
			serialize(Project, project),
		);

		const updatedProject = await projectData.json();
		const projectModel = Project.fromJson(updatedProject);

		setProject(projectModel);

		return project;
	};

	const saveMembers = async (
		action: string,
		userId?: number,
		memberType?: string,
	) => {
		const typeMap: IActionMap = {
			"select-option": "add",
			"deselect-option": "remove",
			"remove-value": "remove",
		};
		if (!project) {
			return;
		}
		const projectData = await api.post(
			`/api/v1/project/${project.id}/update-members`,
			{
				action: typeMap[action],
				user_id: userId,
				member_type: memberType,
			},
		);

		const updatedProject = await projectData.json();
		const projectModel = Project.fromJson(updatedProject);

		project.members = projectModel.members;

		syncProject();

		return project;
	};

	const uploadFiles = async (files: File[], task?: ProjectTask) => {
		const formData = new FormData();

		for (const file of files) {
			formData.append("attachments[]", file, file.name);
		}

		if (task) {
			formData.append("task_id", String(task.id));
		}
		if (!project) {
			return;
		}
		const updatedData = await api.uploadFile(
			`/api/v1/project/${project.id}/upload-files`,
			formData,
		);

		if (task) {
			const updatedTask = await updatedData.json();
			update(ProjectTask, task, updatedTask);
			findTasks();
		} else {
			const updatedProject = await updatedData.json();
			update(Project, project, updatedProject);
			syncProject();
		}
	};

	const deleteFile = async (attachment: Attachment) => {
		if (!project) {
			return;
		}
		const attachmentData = await api.delete(
			`/api/v1/project/${project.id}/attachment/${attachment.id}`,
		);

		const updatedAttachments = await attachmentData.json();
		project.attachments = Attachment.fromJsonArray(
			updatedAttachments.project,
		);
		project.taskAttachments = Attachment.fromJsonArray(
			updatedAttachments.tasks,
		);

		syncProject();
	};

	/**
	 * Change the status of a task
	 *
	 * @param taskId
	 * @param position
	 * @param status
	 */
	const changeTaskStatus = async (
		taskId: number,
		position: number,
		status: string,
	) => {
		const taskData = await api.post(
			`/api/v1/projects/change-task-status/${taskId}`,
			{
				status,
				position,
			},
		);

		let updatedTask = await taskData.json();

		if (taskData.status !== 200) {
			throw Error(updatedTask);
		}
		updatedTask = ProjectTask.fromJson(updatedTask);
		// TODO: Istead of re-fetching tasks here we could have
		// the API return an array of updated tasks id's, positions
		// and statuses and just updated them in task variable in the
		// state.
		findTasks();

		return updatedTask;
	};

	/**
	 * Add a new task to a project
	 *
	 * @param title Task title
	 * @param description Task description
	 */
	const createTask = async (
		title: string,
		description: string,
		status: string,
	) => {
		if (!project) {
			return;
		}
		const taskData = await api.post(
			`/api/v1/project/${project.id}/create-task`,
			{
				title,
				description,
				status,
			},
		);

		const newTask = await taskData.json();

		if (taskData.status !== 200) {
			throw Error(newTask);
		}
		const taskModel = ProjectTask.fromJson(newTask);
		if (tasks) {
			tasks.push(taskModel);
			findTasks();
		}

		return taskModel;
	};

	/**
	 * Find a task by its ID
	 * @param taskId
	 */
	const findTask = async (taskId: number) => {
		const taskData = await api.get(`/api/v1/project/task/${taskId}`);
		const task = await taskData.json();

		return ProjectTask.fromJson(task);
	};

	/**
	 * Save a task to the backend
	 *
	 * @param task
	 */
	const saveTask = async (task: ProjectTask) => {
		const taskData = await api.post(
			`/api/v1/project/task/${task.id}`,
			serialize(ProjectTask, task),
		);

		const updatedTask = await taskData.json();
		update(ProjectTask, task, updatedTask, () => {
			findTasks();
		});

		return task;
	};

	/**
	 * Add or remove a task member
	 * @param task
	 * @param action
	 * @param userId
	 */
	const saveTaskMembers = async (
		task: ProjectTask,
		action: string,
		userId?: number,
	) => {
		const typeMap: IActionMap = {
			"select-option": "add",
			"deselect-option": "remove",
			"remove-value": "remove",
		};

		const taskData = await api.post(
			`/api/v1/project/task/${task.id}/update-members`,
			{
				action: typeMap[action],
				user_id: userId,
			},
		);

		const updatedTask = await taskData.json();
		update(ProjectTask, task, updatedTask, () => {
			findTasks();
		});

		return task;
	};

	/**
	 * Log time on a task
	 * @param task
	 * @param timelog
	 */
	const logTimeOnTask = async (
		task: ProjectTask,
		timelog: ProjectTaskTimelog,
	) => {
		const taskData = await api.post(
			`/api/v1/project/task/${task.id}/log-time`,
			serialize(ProjectTaskTimelog, timelog),
		);

		const updatedTask = await taskData.json();
		const taskModel = ProjectTask.fromJson(updatedTask);
		task.timelogs = taskModel.timelogs;
		task.actions = taskModel.actions;

		if (project) {
			project.timeLogged = project.timeLogged + timelog.hours;
		}

		findTasks();

		return task;
	};

	/**
	 * Find all comments for a task
	 * @param taskId
	 */
	const findProjectTaskComments = async (taskId: number) => {
		const commentData = await api.get(
			`/api/v1/project/task/${taskId}/comments`,
		);
		const comments = await commentData.json();

		return ProjectTaskComment.fromJsonArray(comments);
	};

	/**
	 * Add a new comment to a task
	 * @param taskId
	 * @param comment
	 */
	const createProjectTaskComment = async (
		task: ProjectTask,
		comment: ProjectTaskComment,
	) => {
		const commentData = await api.post(
			`/api/v1/project/task/${task.id}/comment`,
			serialize(ProjectTaskComment, comment),
		);

		const commentJSON = await commentData.json();
		const commentModel = ProjectTaskComment.fromJson(commentJSON);
		task.comments.push(commentModel);

		findTasks();

		return commentModel;
	};

	/**
	 * Update a comment on a task
	 * @param taskId
	 * @param comment
	 */
	const updateProjectTaskComment = async (
		task: ProjectTask,
		comment: ProjectTaskComment,
	) => {
		const commentData = await api.post(
			`/api/v1/project/task/${task.id}/comment/${comment.id}`,
			serialize(ProjectTaskComment, comment),
		);

		const commentJSON = await commentData.json();
		const commentModel = ProjectTaskComment.fromJson(commentJSON);

		return commentModel;
	};

	const saveAddProjectTaskChecklist = async (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
	) => {
		const checklistData = await api.post(
			`/api/v1/project/task/${task.id}/checklist`,
			serialize(ProjectTaskChecklist, checklist),
		);

		const checklistJson = await checklistData.json();
		const checklistModel = ProjectTaskChecklist.fromJson(checklistJson);

		task.checklists.push(checklistModel);

		findTasks();

		return checklistModel;
	};

	const saveUpdateProjectTaskChecklist = async (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
	) => {
		const checklistData = await api.post(
			`/api/v1/project/task/${task.id}/checklist/${checklist.id}`,
			serialize(ProjectTaskChecklist, checklist),
		);

		const checklistJson = await checklistData.json();
		const checklistModel = ProjectTaskChecklist.fromJson(checklistJson);

		checklist.name = checklistModel.name;

		findTasks();

		return checklistModel;
	};

	const deleteProjectTaskChecklist = async (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
	) => {
		const checklistData = await api.delete(
			`/api/v1/project/task/${task.id}/checklist/${checklist.id}`,
		);

		const checklistJson = await checklistData.json();
		const checklistModel = ProjectTaskChecklist.fromJsonArray(
			checklistJson,
		);

		task.checklists = checklistModel;

		findTasks();

		return checklistModel;
	};

	const saveAddProjectTaskChecklistItem = async (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
		item: ProjectTaskChecklistItem,
	) => {
		const itemData = await api.post(
			`/api/v1/project/task/${task.id}/checklist/${checklist.id}/item`,
			serialize(ProjectTaskChecklistItem, item),
		);

		const itemJson = await itemData.json();
		const itemModel = ProjectTaskChecklistItem.fromJson(itemJson);

		checklist.items.push(itemModel);

		findTasks();

		return itemModel;
	};

	const saveUpdateProjectTaskChecklistItem = async (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
		item: ProjectTaskChecklistItem,
	) => {
		const itemData = await api.post(
			`/api/v1/project/task/${task.id}/checklist/${checklist.id}/item/${
				item.id
			}`,
			serialize(ProjectTaskChecklistItem, item),
		);

		const itemJson = await itemData.json();
		item = ProjectTaskChecklistItem.fromJson(itemJson);
		findTasks();

		return item;
	};

	const deleteProjectTaskChecklistItem = async (
		task: ProjectTask,
		checklist: ProjectTaskChecklist,
		item: ProjectTaskChecklistItem,
	) => {
		const itemData = await api.delete(
			`/api/v1/project/task/${task.id}/checklist/${checklist.id}/item/${
				item.id
			}`,
		);

		const itemJson = await itemData.json();
		checklist.items = ProjectTaskChecklistItem.fromJsonArray(itemJson);

		findTasks();

		return checklist.items;
	};

	const toggleWatchingTask = async (task: ProjectTask) => {
		const newTask = await taskService.toggleWatching(task);

		task.watchers = newTask.watchers;
		findTasks();

		return newTask;
	};

	const toggleWatchingProject = async () => {
		if (!project) {
			return;
		}
		const newProject = await projectService.toggleWatching(project);

		setProject(newProject);

		return newProject;
	};

	const syncProject = () => {
		setProject(_.cloneDeep(project));
	};

	const value = {
		setProject,
		create,
		save,
		find,
		findTasks,
		uploadFiles,
		deleteFile,
		project,
		changeTaskStatus,
		createTask,
		setTasks,
		findTask,
		saveTask,
		saveTaskMembers,
		tasks,
		logTimeOnTask,
		saveMembers,
		findProjectTaskComments,
		createProjectTaskComment,
		updateProjectTaskComment,
		saveAddProjectTaskChecklist,
		deleteProjectTaskChecklist,
		saveUpdateProjectTaskChecklist,
		saveAddProjectTaskChecklistItem,
		deleteProjectTaskChecklistItem,
		saveUpdateProjectTaskChecklistItem,
		toggleWatchingTask,
		toggleWatchingProject,
	};

	return (
		<ProjectContext.Provider value={value}>
			{props.children}
		</ProjectContext.Provider>
	);
};

export const useProjects = () => {
	return useContext(ProjectContext);
};
